import {SupportedLocale} from '@/initializers/i18next';
import {DocumentData, DocumentModel, DocumentValue, MetaValue, ValueResolver} from '../../engine/types';
import {TypeMap} from '../utils';

export type FieldError = {
  error: string;
  extra?: Record<string, unknown>;
};

export type Validity =
  | {valid: true; touched?: boolean}
  | {valid: true; touched: false; willBeValidOnceTouched: boolean}
  | {
      valid: false;
      touched?: boolean;
      errors: FieldError[];
    };

export type SectionValidity = Validity & {
  fields: {
    [fieldId: string]: Validity;
  };
};

export type DocumentValidity = {
  [sectionId: string]: SectionValidity;
};

export type DocumentAttachments = {
  [sectionId: string]: {fields: {[fieldId: string]: string}};
};

export interface DocumentNavigation {
  returnToPreviousModalId?: string;
}

export interface DocumentState {
  data: DocumentData['data'];
  attachments: DocumentAttachments;
  meta: MetaValue;
  validity: DocumentValidity;
  locale: SupportedLocale;
  navigation: DocumentNavigation;
}

type ValidateFieldOptions = {
  valueResolver: ValueResolver;
  documentModel: DocumentModel;
};

type TouchFieldOptions = {
  documentModel: DocumentModel;
};

export enum ValidateSectionMode {
  IgnoreFields = 'ignoreFields',
  OnlyTouchedFields = 'onlyTouchedFields',
  All = 'all',
  Fields = 'fields',
}

type ValidateSectionOptions = ValidateFieldOptions & {
  validateMode?: ValidateSectionMode;
  validateFields?: string[];
};

export const BATCH_OPERATIONS: TypeMap<'type', BatchUpdate> = {
  SET: 'set',
  UNSET: 'unset',
  UNSET_VALIDITY: 'unset_validity',
  MOVE: 'move',
  INCREMENT: 'increment',
  DECREMENT: 'decrement',
};

export type BatchUpdate =
  | {type: 'set'; id: string; value: DocumentValue | DocumentValue[]}
  | {type: 'unset'; id: string}
  | {type: 'unset_validity'; sectionId: string; fieldId: string}
  | {type: 'move'; fromId: string; toId: string}
  | {type: 'increment'; id: string; value?: number}
  | {type: 'decrement'; id: string; value?: number};

export type DocumentStateMethods = {
  updateMeta: (fieldId: keyof MetaValue, value: string) => void;
  clearSection: (sectionId: string, documentModel: DocumentModel) => void;
  clearForm: (documentModel: DocumentModel) => void;
  batchUpdate: (patch: BatchUpdate[]) => void;
  registerAttachment: (sectionId: string, fieldId: string, value: string) => void;
  unregisterAttachment: (sectionId: string, fieldId: string) => void;
  unregisterAttachments: () => void;
  unregisterSectionAttachments: (sectionId: string) => void;
  updateField: (fieldId: string, value: DocumentValue | DocumentValue[]) => void;
  unsetField: (fieldId: string) => void;
  setSectionValidity: (sectionId: string, isValid: boolean, errors?: FieldError[]) => void;
  validateSection: (sectionId: string, options: ValidateSectionOptions) => boolean;
  validateField: (sectionId: string, fieldId: string, options: ValidateFieldOptions) => boolean;
  touchField: (sectionId: string, fieldId: string, options: TouchFieldOptions) => boolean;
  changeLocale: (locale: SupportedLocale) => void;
  setReturnToPreviousModalId: (modalGroupId?: string) => void;
};
